import { useMemo } from 'react';

import { orderAdvertisersBySpend } from '../../../utils/orderAdvertisers';
import { cpmFrom, sumRows, trendPoints, type MediaMetric, type TrendPoint } from './aggregate';
import {
  advertiserDimensionRows,
  displayDimensions,
  marketDimensionRows,
  type DimensionKind,
  type DimensionRow,
  type MarketDimensionRow,
} from './dimensions';
import {
  availableMonths,
  inMonthRange,
  resolveAdvertisers,
  type MediaLandscapeFilters,
} from './filters';
import {
  overIndexInsights,
  trendInsights,
  type OverIndexInsight,
  type TrendInsight,
} from './insights';
import type { PathmaticsData, PathmaticsMonthlyRow } from './pathmaticsData.schema';

export interface MediaLandscapeDimensionSet {
  advertiser: DimensionRow[];
  market: MarketDimensionRow[];
  /**
   * Over-index facts for this kind alone. The bars card follows its own channels/publishers
   * toggle, so a single set on the view would caption publisher bars with channel prose.
   */
  overIndex: OverIndexInsight[];
}

export interface MediaLandscapeView {
  months: string[];
  monthsInRange: string[];
  orderedAdvertisers: string[];
  selectedAdvertisers: string[];
  monthlyRows: PathmaticsMonthlyRow[];
  hasRows: boolean;
  kpis: { spend: number; impressions: number; cpm: number };
  trend: TrendPoint[];
  trendInsights: TrendInsight[];
  channels: MediaLandscapeDimensionSet;
  publishers: MediaLandscapeDimensionSet;
}

export function useMediaLandscapeData(
  data: PathmaticsData,
  filters: MediaLandscapeFilters,
  metric: MediaMetric,
): MediaLandscapeView {
  return useMemo(() => {
    const { monthly } = data.mediaLandscape;
    const months = availableMonths(monthly);

    // Ranked over the full range on purpose: colour keys off position, so ranking by
    // filtered spend would recolour a brand's series as the user filters.
    const orderedAdvertisers = orderAdvertisersBySpend(data.availableAdvertisers, monthly);
    const selectedAdvertisers = resolveAdvertisers(orderedAdvertisers, filters.advertisers);
    const selected = new Set(selectedAdvertisers);

    const monthlyRows = inMonthRange(monthly, filters.from, filters.to).filter((r) =>
      selected.has(r.advertiser),
    );
    const monthsInRange = months.filter((month) => month >= filters.from && month <= filters.to);

    const dimensionSet = (kind: DimensionKind): MediaLandscapeDimensionSet => {
      const advertiser = inMonthRange(
        advertiserDimensionRows(data, kind),
        filters.from,
        filters.to,
      ).filter((r) => selected.has(r.advertiser));
      // Market rows are never advertiser-filtered: they are the denominator the
      // selection is measured against.
      const market = inMonthRange(marketDimensionRows(data, kind), filters.from, filters.to);
      return {
        advertiser,
        market,
        overIndex: overIndexInsights(
          advertiser,
          market,
          selectedAdvertisers,
          displayDimensions(advertiser),
        ),
      };
    };

    const channels = dimensionSet('channels');
    const publishers = dimensionSet('publishers');
    const totals = sumRows(monthlyRows);

    return {
      months,
      monthsInRange,
      orderedAdvertisers,
      selectedAdvertisers,
      monthlyRows,
      hasRows: monthlyRows.length > 0,
      kpis: {
        spend: totals.spend,
        impressions: totals.impressions,
        cpm: cpmFrom(totals.spend, totals.impressions),
      },
      trend: trendPoints(monthlyRows, selectedAdvertisers, monthsInRange, metric),
      trendInsights: trendInsights(
        monthlyRows,
        channels.advertiser,
        selectedAdvertisers,
        monthsInRange,
        metric,
      ),
      channels,
      publishers,
    };
  }, [data, filters, metric]);
}
